import { Fragment, useState, type ReactNode } from "react";
import { cn } from "~/lib/utils";
import { useColumnWidths } from "./columnResize";
import { DataTableHeaderCell } from "./DataTableHeaderCell";
import { PlanningGroupHeader } from "./PlanningGroupHeader";

export interface DataTableColumn {
  readonly id: string;
  readonly header: string;
  readonly defaultWidth: number;
  /** Let this column absorb the table's remaining width. */
  readonly fill?: boolean;
  /** Drag-resizable? Default true unless this is the fill column. */
  readonly resizable?: boolean;
  /** Present ⇒ the header is a sort control for this column id. */
  readonly sortable?: boolean;
}

/** A grouping bucket: stable key, header label, and sort order (lower = first). */
export interface DataTableGroup {
  readonly key: string;
  readonly label: string;
  readonly order: number;
  /** Optional leading glyph (status/priority) for the rounded group bar. */
  readonly leading?: ReactNode;
}

export type DataTableSort = { readonly columnId: string; readonly direction: "asc" | "desc" };

export interface DataTableProps<Row> {
  readonly columns: ReadonlyArray<DataTableColumn>;
  readonly rows: ReadonlyArray<Row>;
  /** Renders the full <tr> for a row (with its own key) — the surface owns cells. */
  readonly renderRow: (row: Row) => ReactNode;
  /** localStorage key for persisted column widths (per surface). */
  readonly widthsStorageKey: string;
  /** Active sort (controlled by the surface, which owns the comparison logic). */
  readonly sort: DataTableSort | null;
  readonly onSortChange: (sort: DataTableSort | null) => void;
  /** Provide to group rows into collapsible sections. Returns the bucket per row. */
  readonly groupOf?: ((row: Row) => DataTableGroup) | undefined;
  /** Authoritative groups to render even when they contain no rows. */
  readonly groupCatalog?: ReadonlyArray<DataTableGroup> | undefined;
  /** Optional quick-add for a group (Linear `+` on the group bar). */
  readonly onCreateInGroup?: ((group: DataTableGroup) => void) | undefined;
  readonly canCreateInGroup?: ((group: DataTableGroup) => boolean) | undefined;
  readonly createInGroupLabel?: ((group: DataTableGroup) => string) | undefined;
  readonly emptyLabel: string;
  readonly minWidthPx?: number;
  readonly footer?: ReactNode;
  /** Optional non-table body shown on narrow viewports (e.g. a card list). */
  readonly mobileBody?: ReactNode;
}

/**
 * Reusable data-table shell: sortable + drag-resizable columns, optional
 * collapsible grouping, per-surface persisted widths. The surface owns row
 * rendering (via renderRow) and the sort comparison (rows arrive pre-sorted);
 * this component owns the table chrome so every page's table looks and behaves
 * identically. See ProjectTaskListView for a full integration.
 */
export function DataTable<Row>({
  columns,
  rows,
  renderRow,
  widthsStorageKey,
  sort,
  onSortChange,
  groupOf,
  groupCatalog,
  onCreateInGroup,
  canCreateInGroup,
  createInGroupLabel,
  emptyLabel,
  minWidthPx = 760,
  footer,
  mobileBody,
}: DataTableProps<Row>) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const widthDefaults = Object.fromEntries(
    columns
      .filter((c) => c.resizable !== false && c.fill !== true)
      .map((c) => [c.id, c.defaultWidth]),
  );
  const { widths, setColumnWidth, resetColumn } = useColumnWidths(widthsStorageKey, widthDefaults);

  const groups = groupOf ? groupRows(rows, groupOf, groupCatalog) : null;

  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onSortHeader = (columnId: string) => {
    if (sort?.columnId === columnId) {
      onSortChange({ columnId, direction: sort.direction === "asc" ? "desc" : "asc" });
    } else {
      onSortChange({ columnId, direction: "asc" });
    }
  };

  return (
    <>
      {mobileBody ? <div className="min-h-0 flex-1 sm:hidden">{mobileBody}</div> : null}
      <div className={cn("min-h-0 flex-1 overflow-auto", mobileBody ? "hidden sm:block" : null)}>
        <table
          className="w-full table-fixed border-separate border-spacing-0 text-sm"
          style={{ minWidth: `${minWidthPx}px` }}
        >
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.id}
                style={
                  column.fill === true
                    ? undefined
                    : { width: `${widths[column.id] ?? column.defaultWidth}px` }
                }
              />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="bg-background/95 shadow-[inset_0_-1px_0_hsl(var(--border)/0.55)] backdrop-blur">
              {columns.map((column) => (
                <DataTableHeaderCell
                  key={column.id}
                  column={column}
                  width={widths[column.id] ?? column.defaultWidth}
                  sort={sort}
                  onSort={onSortHeader}
                  onWidthChange={setColumnWidth}
                  onResetWidth={resetColumn}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {groups != null && groups.length > 0 ? (
              groups.map((group) => {
                const isCollapsed = collapsed.has(group.key);
                const tableGroup: DataTableGroup = {
                  key: group.key,
                  label: group.label,
                  order: group.order,
                  leading: group.leading,
                };
                return (
                  <Fragment key={group.key}>
                    <tr>
                      <td colSpan={columns.length} className="px-2 pb-0.5 pt-2 first:pt-1.5">
                        <PlanningGroupHeader
                          label={group.label}
                          count={group.rows.length}
                          expanded={!isCollapsed}
                          onToggle={() => toggleGroup(group.key)}
                          leading={group.leading}
                          onCreate={
                            onCreateInGroup != null && (canCreateInGroup?.(tableGroup) ?? true)
                              ? () => onCreateInGroup(tableGroup)
                              : undefined
                          }
                          createLabel={
                            createInGroupLabel != null ? createInGroupLabel(tableGroup) : undefined
                          }
                        />
                      </td>
                    </tr>
                    {isCollapsed ? null : group.rows.map((row) => renderRow(row))}
                  </Fragment>
                );
              })
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row) => renderRow(row))
            )}
          </tbody>
        </table>
      </div>
      {footer}
    </>
  );
}

interface RenderGroup<Row> {
  readonly key: string;
  readonly label: string;
  readonly order: number;
  readonly leading: ReactNode | undefined;
  readonly rows: Row[];
}

function groupRows<Row>(
  rows: ReadonlyArray<Row>,
  groupOf: (row: Row) => DataTableGroup,
  groupCatalog: ReadonlyArray<DataTableGroup> = [],
): ReadonlyArray<RenderGroup<Row>> {
  const buckets = new Map<
    string,
    { label: string; order: number; leading: ReactNode | undefined; rows: Row[] }
  >(
    groupCatalog.map((group) => [
      group.key,
      { label: group.label, order: group.order, leading: group.leading, rows: [] },
    ]),
  );
  for (const row of rows) {
    const group = groupOf(row);
    const bucket = buckets.get(group.key) ?? {
      label: group.label,
      order: group.order,
      leading: group.leading,
      rows: [],
    };
    bucket.rows.push(row);
    buckets.set(group.key, bucket);
  }
  return [...buckets.entries()]
    .toSorted(([, a], [, b]) => a.order - b.order || a.label.localeCompare(b.label))
    .map(([key, bucket]) => ({
      key,
      label: bucket.label,
      order: bucket.order,
      leading: bucket.leading,
      rows: bucket.rows,
    }));
}
